// ============================================================================
// OBP : carrousel d'upsell du tiroir panier « Un autre cadeau à faire ? ».
// Remplace l'upsell de l'app EA Sticky Cart, avec LA différence clé :
// les prix affichés sont les prix REMISÉS par le moteur d'offres maison
// (simulation locale via obp-cart-rules.js ; la vraie remise est appliquée
// par la Function OBP Discounts au checkout).
//
// Rendu 100 % client : le HTML serveur ne contient que la coquille
// <obp-cart-upsell>. Après chaque morph/re-render du tiroir (Horizon),
// la coquille revient vide → on re-render (MutationObserver + cartUpdate).
// ============================================================================
import { ThemeEvents, CartUpdateEvent, CartAddEvent } from '@theme/events';
// via l'importmap (scripts.liquid) : un import relatif donnerait une URL sans
// ?v=, que le CDN Shopify passe dans un minifieur legacy es5 qui échoue.
import {
  priceIfAdded,
  companionsForCart,
  suggestionsForCart,
  OBP_COMPLEMENTARY_PRODUCTS,
} from '@theme/obp-cart-rules';

const MAX_UPSELLS = 5;

class ObpCartUpsell extends HTMLElement {
  /** @type {AbortController | null} */
  #abort = null;
  #renderToken = 0;
  #scheduled = null;
  #selfMutation = false;
  /** @type {Map<string, any>} cache produit par handle (session tiroir) */
  static #productCache = new Map();

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;
    document.addEventListener(ThemeEvents.cartUpdate, this.#scheduleRender, { signal });

    // Le morph de section Horizon remet la coquille serveur (vide) : on re-render.
    const observer = new MutationObserver(() => {
      if (this.#selfMutation) return;
      this.#scheduleRender();
    });
    observer.observe(this, { childList: true });
    signal.addEventListener('abort', () => observer.disconnect());

    this.#render();

    // Filet de sécurité : au chargement d'une page avec un panier DÉJÀ rempli
    // (visiteur qui revient), le morph d'hydratation Horizon peut vider notre
    // coquille juste après le 1er render, sans qu'un cartUpdate ne suive.
    // On re-render une fois la section stabilisée (#render est idempotent).
    setTimeout(() => {
      if (!this.querySelector('.obp-upsell__card')) this.#render();
    }, 600);
  }

  disconnectedCallback() {
    this.#abort?.abort();
  }

  #scheduleRender = () => {
    clearTimeout(this.#scheduled);
    this.#scheduled = setTimeout(() => this.#render(), 250);
  };

  async #render() {
    const token = ++this.#renderToken;
    try {
      const cart = await fetch('/cart.js').then((r) => r.json());
      if (token !== this.#renderToken) return;
      if (!cart.items?.length) {
        this.#write('');
        return;
      }

      const candidates = await this.#collectCandidates(cart);
      if (token !== this.#renderToken) return;
      if (!candidates.length) {
        this.#write('');
        return;
      }

      this.#writeCarousel(cart, candidates);
    } catch (error) {
      console.error('[OBP upsell]', error);
    }
  }

  /**
   * Compagnons remisés des règles d'abord, puis recommandations Shopify,
   * hors produits déjà au panier, max 5.
   */
  async #collectCandidates(cart) {
    const inCartProducts = new Set(cart.items.map((item) => String(item.product_id)));
    const inCartVariants = new Set(cart.items.map((item) => String(item.variant_id)));
    /** @type {Array<{product: any, variant: any}>} */
    const pool = [];
    const pooledVariants = new Set();

    // Ajoute une VARIANTE précise (sert à compléter une collection).
    const pushVariant = (product, variant) => {
      if (!product || !variant) return;
      if (variant.available === false) return;
      const vid = String(variant.id);
      if (inCartVariants.has(vid) || pooledVariants.has(vid)) return;
      pooledVariants.add(vid);
      pool.push({ product, variant });
    };
    // Ajoute un PRODUIT (variante = 1re disponible), hors produits déjà au panier.
    const pushProduct = (product) => {
      if (!product || !product.available) return;
      if (inCartProducts.has(String(product.id))) return;
      const variant = (product.variants ?? []).find((v) => v.available) ?? product.variants?.[0];
      pushVariant(product, variant);
    };

    // 1. COMPLÉTER LA COLLECTION (priorité) : autre(s) variante(s) d'un produit
    //    « à réunir » déjà au panier — Coffret 1 → Coffret 2, Boîte 1 → Boîte 2.
    const toComplete = cart.items.filter((item) => OBP_COMPLEMENTARY_PRODUCTS.has(item.product_id));
    const completeProducts = await Promise.all(toComplete.map((item) => this.#fetchProduct(item.handle)));
    completeProducts.forEach((product) => {
      (product?.variants ?? []).forEach((variant) => pushVariant(product, variant));
    });

    // 2. Compagnons remisés (règles bxgy déclenchées par le panier).
    const companions = companionsForCart(cart.items).filter((c) => c.handle);
    const companionProducts = await Promise.all(companions.map((c) => this.#fetchProduct(c.handle)));
    companionProducts.forEach(pushProduct);

    // 3. Recommandations Shopify (vides sur cette boutique : filet de sécurité).
    if (pool.length < MAX_UPSELLS) {
      try {
        const recoUrl = `/recommendations/products.json?product_id=${cart.items[0].product_id}&limit=10`;
        const reco = await fetch(recoUrl).then((r) => r.json());
        (reco.products ?? []).forEach(pushProduct);
      } catch {
        // pas bloquant : les compagnons et suggestions maison suffisent
      }
    }

    // 4. Même univers puis autres (familles maison).
    if (pool.length < MAX_UPSELLS) {
      const suggestions = suggestionsForCart(cart.items);
      const needed = suggestions.slice(0, MAX_UPSELLS - pool.length + 2);
      const products = await Promise.all(needed.map((s) => this.#fetchProduct(s.handle)));
      products.forEach(pushProduct);
    }

    return pool.slice(0, MAX_UPSELLS);
  }

  async #fetchProduct(handle) {
    const cache = ObpCartUpsell.#productCache;
    if (!cache.has(handle)) {
      const promise = fetch(`/products/${handle}.js`)
        .then((r) => (r.ok ? r.json() : null))
        .catch(() => null);
      cache.set(handle, promise);
    }
    return cache.get(handle);
  }

  #writeCarousel(cart, candidates) {
    const wrap = document.createElement('div');
    wrap.className = 'obp-upsell';

    const head = document.createElement('div');
    head.className = 'obp-upsell__head';
    const title = document.createElement('p');
    title.className = 'obp-upsell__title';
    title.textContent = 'Un autre cadeau à faire ?';
    head.appendChild(title);

    const arrows = document.createElement('div');
    arrows.className = 'obp-upsell__arrows';
    const prev = this.#arrowButton('Produits précédents', 'M15 18l-6-6 6-6');
    const next = this.#arrowButton('Produits suivants', 'M9 6l6 6-6 6');
    arrows.append(prev, next);
    head.appendChild(arrows);
    wrap.appendChild(head);

    const track = document.createElement('div');
    track.className = 'obp-upsell__track';

    for (const { product, variant } of candidates) {
      if (!variant) continue;
      const unitCents = toCents(variant.price ?? product.price);
      const { discountCents, discountedUnitCents } = priceIfAdded(cart.items, product.id, unitCents);
      track.appendChild(this.#card(product, variant, unitCents, discountCents, discountedUnitCents));
    }

    wrap.appendChild(track);
    prev.addEventListener('click', () => track.scrollBy({ left: -track.clientWidth, behavior: 'smooth' }));
    next.addEventListener('click', () => track.scrollBy({ left: track.clientWidth, behavior: 'smooth' }));

    this.#write(wrap);
  }

  #card(product, variant, unitCents, discountCents, discountedUnitCents) {
    const card = document.createElement('article');
    card.className = 'obp-upsell__card';

    const imgSrc = product.featured_image?.src ?? product.featured_image ?? product.images?.[0];
    if (imgSrc) {
      const img = document.createElement('img');
      img.className = 'obp-upsell__img';
      img.loading = 'lazy';
      img.width = 64;
      img.height = 64;
      img.alt = '';
      img.src = `${imgSrc}${String(imgSrc).includes('?') ? '&' : '?'}width=160`;
      card.appendChild(img);
    }

    const info = document.createElement('div');
    info.className = 'obp-upsell__info';

    const name = document.createElement('p');
    name.className = 'obp-upsell__name';
    let label = String(product.title).replace(/ — /g, ' - ');
    // Produit multi-variantes (coffret, dessous-de-verre) : on annonce clairement
    // LA variante proposée (« Coffret 2 · … ») pour que le complément soit lisible.
    const multiVariant = (product.variants ?? []).length > 1;
    if (multiVariant && variant.title && variant.title !== 'Default Title') {
      const shortVariant = variant.title.split(' — ')[0].split(' - ')[0].trim();
      label = `${shortVariant} · ${label}`;
    }
    name.textContent = label;
    info.appendChild(name);

    const prices = document.createElement('p');
    prices.className = 'obp-upsell__prices';
    const price = document.createElement('span');
    price.className = 'obp-upsell__price';
    price.textContent = formatMoney(discountedUnitCents);
    prices.appendChild(price);
    if (discountCents > 0) {
      const compare = document.createElement('s');
      compare.className = 'obp-upsell__compare';
      compare.textContent = formatMoney(unitCents);
      prices.appendChild(compare);
      const saving = document.createElement('span');
      saving.className = 'obp-upsell__saving';
      saving.textContent = `−${formatMoney(discountCents)}`;
      prices.appendChild(saving);
    }
    info.appendChild(prices);
    card.appendChild(info);

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'obp-upsell__add';
    button.textContent = 'Ajouter';
    button.addEventListener('click', () => this.#addToCart(button, variant.id));
    card.appendChild(button);

    return card;
  }

  #arrowButton(label, path) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'obp-upsell__arrow';
    button.setAttribute('aria-label', label);
    button.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="${path}"/></svg>`;
    return button;
  }

  async #addToCart(button, variantId) {
    button.disabled = true;
    button.textContent = '…';
    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
      });
      if (!response.ok) throw new Error(`add.js ${response.status}`);
      const cart = await fetch('/cart.js').then((r) => r.json());
      // Sans payload `sections` : cart-items-component re-render le tiroir
      // via le section renderer (même mécanique qu'obp-offers.js).
      document.dispatchEvent(
        new CartUpdateEvent(cart, 'obp-cart-upsell', {
          source: 'obp-cart-upsell',
          itemCount: cart.item_count,
        })
      );
    } catch (error) {
      console.error('[OBP upsell] ajout impossible', error);
      button.disabled = false;
      button.textContent = 'Ajouter';
    }
  }

  /** Écrit le contenu en neutralisant notre propre MutationObserver. */
  #write(content) {
    this.#selfMutation = true;
    if (content === '') {
      this.replaceChildren();
    } else {
      this.replaceChildren(content);
    }
    // setTimeout (PAS requestAnimationFrame : jamais déclenché dans un onglet
    // masqué/throttlé) : le MutationObserver, en microtâche, voit nos propres
    // mutations AVANT ce timeout et les ignore bien.
    setTimeout(() => {
      this.#selfMutation = false;
    }, 0);
  }
}

/** Les endpoints .js/.json renvoient des centimes ; garde-fou si chaîne. */
function toCents(value) {
  if (typeof value === 'string') return Math.round(parseFloat(value) * 100) || 0;
  return value || 0;
}

function formatMoney(cents) {
  return `${(cents / 100).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
}

if (!customElements.get('obp-cart-upsell')) {
  customElements.define('obp-cart-upsell', ObpCartUpsell);
}

// ============================================================================
// OBP : code promo arrivé par lien (?discount=CODE, ou cookie discount_code
// posé par /discount/CODE). On l'applique au PANIER (cart/update.js, même
// mécanique que cart-discount.js) pour que le tiroir affiche la pastille du
// code et les totaux remisés, au lieu d'attendre le checkout.
// ============================================================================
let urlDiscountDone = false;

async function applyUrlDiscount() {
  if (urlDiscountDone) return;
  let code = new URLSearchParams(window.location.search).get('discount');
  if (!code) {
    const match = document.cookie.match(/(?:^|;\s*)discount_code=([^;]+)/);
    if (match) code = decodeURIComponent(match[1]);
  }
  if (!code) {
    urlDiscountDone = true;
    return;
  }

  try {
    const cart = await fetch('/cart.js').then((r) => r.json());
    // un code ne s'applique qu'à un panier non vide : on retentera après le 1er ajout
    if (!cart.item_count) return;

    const existing = (cart.discount_codes ?? []).map((d) => d.code);
    if (existing.some((c) => c.toUpperCase() === code.toUpperCase())) {
      urlDiscountDone = true;
      return;
    }

    const response = await fetch(Theme.routes.cart_update_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ discount: [...existing, code].join(',') }),
    });
    const data = await response.json();
    urlDiscountDone = true;

    const applied = (data.discount_codes ?? []).some(
      (d) => d.code.toUpperCase() === code.toUpperCase() && d.applicable
    );
    if (applied) {
      document.dispatchEvent(
        new CartUpdateEvent(data, 'obp-cart-upsell', {
          source: 'obp-url-discount',
          itemCount: data.item_count,
        })
      );
    }
  } catch (error) {
    console.error('[OBP code promo URL]', error);
  }
}

applyUrlDiscount();
document.addEventListener(CartAddEvent.eventName, () => {
  // léger délai : laisse l'ajout aboutir côté panier
  setTimeout(applyUrlDiscount, 300);
});
